import React, { useMemo, Suspense, useEffect, useState } from "react";
import "./home.css";
import { useGetHomeQuery, apiSlice } from "../../features/api/apiSlice";
import { useDispatch } from "react-redux";
import PopUpOnce from "../../component/PopUpOnce";
import Loader from "../../utils/Loader";

// ✅ Lazy-load large components to reduce bundle size
const Tabs = React.lazy(() => import("../../component/Tabs"));
const KeyContact = React.lazy(() => import("../../component/keycontact"));
const LandingSlider = React.lazy(() => import("../../component/LandingSlider"));
const Accordion = React.lazy(() => import("../../component/Accordion"));
const ServiceTabs = React.lazy(() => import("../../component/ServiceTabs"));
const Interest = React.lazy(() => import("../../component/Interest"));
const Videos = React.lazy(() => import("../../component/Videos"));
const Offers = React.lazy(() => import("../../component/Offers"));
const MobileBank = React.lazy(() => import("../../component/MobileBank"));

const Home = () => {
  const dispatch = useDispatch();

  // ✅ Default empty structure for instant render
  const defaultData = useMemo(
    () => ({
      banners: [],
      news: [],
      audio: [],
      grievance_officer: null,
      information_officer: null,
      compliance_officer: null,
      company_secretary: null,
      popups: [],
      service_category: [],
      interest_rates: [],
      videos: [],
      offers_category: [],
      mobile_bank: null,
    }),
    []
  );

  const {
    data = defaultData,
    isFetching,
    isLoading, // ✅ true only on initial load
    isError,
    error,
  } = useGetHomeQuery(undefined, {
    refetchOnMountOrArgChange: false,
    refetchOnReconnect: true,
    pollingInterval: 300000, // background refresh every 5 minutes
  });

  // ✅ Prefetch in background for next navigation
  useEffect(() => {
    dispatch(apiSlice.util.prefetch("getHome", undefined, { force: false }));
  }, [dispatch]);

  // ✅ Handle popups
  const [popups, setPopups] = useState([]);
  useEffect(() => {
    setPopups(Array.isArray(data.popups) ? data.popups : []);
  }, [data.popups]);

  // ✅ Memoized derived data
  const banners = useMemo(() => data.banners.slice(0, 5), [data.banners]);
  const news = useMemo(() => data.news.slice(0, 5), [data.news]);
  const grievance = useMemo(
    () => data.grievance_officer,
    [data.grievance_officer]
  );
  const information = useMemo(
    () => data.information_officer,
    [data.information_officer]
  );
  const compliance = useMemo(
    () => data.compliance_officer,
    [data.compliance_officer]
  );
  const secretary = useMemo(
    () => data.company_secretary,
    [data.company_secretary]
  );

  // ✅ 1️⃣ Global Loading Screen (before data loads)
  if (isLoading) {
    return <Loader />;
  }

  // ✅ 2️⃣ Error Handling
  if (isError) {
    return (
      <div className="errorContainer">
        <p>Something went wrong: {error?.message || "Unknown error"}</p>
      </div>
    );
  }

  // ✅ 3️⃣ Render Page Content
  return (
    <>
      {/* Show Popups if any */}
      {popups.length > 0 && <PopUpOnce values={popups} />}

      <div className="pt" />

      <div className="row-divider">
        <div className="slider">
          <Suspense fallback={<div className="section-loader">...</div>}>
            <LandingSlider values={banners} />
          </Suspense>
        </div>

        <div className="accordion">
          <Suspense fallback={<div className="section-loader">...</div>}>
            <Accordion
              news={news}
              audio={data.audio}
              grievance={grievance}
              information={information}
              compliance={compliance}
              secretary={secretary}
            />
          </Suspense>
        </div>
      </div>

      <Suspense fallback={<div className="section-loader">...</div>}>
        <Tabs />
      </Suspense>

      {data.service_category?.length > 0 && (
        <Suspense fallback={<div className="section-loader">...</div>}>
          <ServiceTabs values={data.service_category} />
        </Suspense>
      )}

      {data.interest_rates?.length > 0 && (
        <Suspense fallback={<div className="section-loader">...</div>}>
          <Interest values={data.interest_rates} />
        </Suspense>
      )}

      {data.videos?.length > 0 && (
        <Suspense fallback={<div className="section-loader">...</div>}>
          <Videos values={data.videos} />
        </Suspense>
      )}

      {data.offers_category?.length > 0 && (
        <Suspense fallback={<div className="section-loader">...</div>}>
          <Offers values={data.offers_category} />
        </Suspense>
      )}

      <Suspense fallback={<div className="section-loader">...</div>}>
        <KeyContact
          grievance={grievance}
          information={information}
          compliance={compliance}
          secretary={secretary}
        />
      </Suspense>

      {data.mobile_bank && (
        <Suspense fallback={<div className="section-loader">...</div>}>
          <MobileBank />
        </Suspense>
      )}

      {/* ✅ Small indicator for background refresh */}
      {isFetching && <div className="refresh-indicator">...</div>}
    </>
  );
};

export default Home;
